import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import { isEmpty } from '@antv/util';
import { NativeResources } from '@ariksa/abac/api';
import { V1CloudEventsResourceRow } from '@ariksa/audittrail/api';
import { CloudProvider } from '@ariksa/inventory-core/api';
import { Box, HStack, Stack } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { map } from 'lodash';
import { DateRange } from 'react-day-picker';
import { useDispatch, useSelector } from 'react-redux';
import { colorMap, colorOpacity, customTheme } from 'theme';

import { PageHeaderWithIcon, StackedHeader } from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { OutlinedForm } from 'components/DataEntry';
import {
  ExclamationCircleIcon,
  getIconColor,
  IconTypes,
  InformationAlert,
  renderIconWithLabel,
} from 'components/index';
import { Drawer } from 'components/Overlay';
import { useMetadataDrawerTabs } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/hooks/useMetadataDrawerTabs';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { useRedirectToSecurityGraph } from 'containers/Visibility/SecurityGraphNext/hooks/useRedirectToSecurityGraph';

import { selectIncidentResponse } from '../../../selectors';
import { actions } from '../../../slice';
import { ObjectPathMenuList } from '../../utils';
import {
  flattenPaths,
  flattenSelectedPaths,
  getFolderOptionsWithSelection,
  getSelectedFolderCount,
  normalizeSelectedFolders,
} from '../ActivityGroups/utils';
import { formatCloudEventsSummaryDateRange } from '../Summary/utils';

import { AccessSummary } from './AccessSummary';
import { ActivityGraph } from './ActivityGraph';
import { ActivityGraphControls } from './ActivityGraphControls';
import { ActivityGraphTable } from './ActivityGraphTable';
import { useActivityGraphDownload } from './useActivityGraphDownload';
import { getActivityAccessFilters } from './utils';

interface Props {
  activityGroupId?: string;
  currentResource?: V1CloudEventsResourceRow;
  isOpen: boolean;
  onClose: () => void;
  resourceType: NativeResources;
  resourceInsight?: Record<string, any>;
}

const ROOT_FOLDER_OPTION = { label: '/', value: '/' };

// Async pagination is temporarily disabled. The previous page size was 15.

export const CloudEventsActivityDrawer = ({
  resourceInsight,
  activityGroupId,
  currentResource,
  isOpen,
  onClose,
  resourceType,
}: Props) => {
  const dispatch = useDispatch();
  const {
    activityGraph,
    activityGroupBucketsByArn,
    activityObjectPaths,
    activityBucketContexts,
  } = useSelector(selectIncidentResponse);
  const { getCloudNativeName } = useResourceType();
  const { onOpenMetadata } = useActiveResourceContext();
  const { accountMapping, cloudAccountIDMapping } = useCloudAccountId();
  const { getTabIndex } = useMetadataDrawerTabs();
  const { updateActiveResource } = useActiveResourceActions();
  const { redirectToSecurityGraph } = useRedirectToSecurityGraph();
  const [state, updateState] = useReducer(
    (previous, next) => ({ ...previous, ...next }),
    {
      // Keep the initial request and date picker on the same inclusive 7-day range.
      dateRange: {
        from: dayjs().subtract(6, 'day').startOf('day').toDate(),
        to: dayjs().endOf('day').toDate(),
      } as DateRange,
      objectPaths: [],
      contexts: [],
      loggingStatusMessage: undefined,
    },
  );
  const [isFullWindow, setIsFullWindow] = useState(false);
  const [isTableView, setIsTableView] = useState(false);
  // Request keys prevent duplicate calls when the drawer re-renders. Each key
  // stores the inputs for one independent activity-data request.
  // Bucket contexts: selected bucket UUID and account.
  const requestedActivityBucketContextsKey = useRef<string>();
  // Object paths: selected bucket ARN/name and resolved cloud account ID.
  const requestedActivityObjectPathsKey = useRef<string>();
  // Activity graph: activity group ID and selected resource ARN.
  const requestedActivityGraphKey = useRef<string>();
  // Identity count: activity group, resource ARN, date range, and folders.
  const requestedActivityIdentityCountKey = useRef<string>();

  // CSV download of the activity graph accesses (see useActivityGraphDownload).
  const downloadActivityGraph = useActivityGraphDownload({
    resourceArn: currentResource?.resource_arn,
    resourceName: currentResource?.resource_name,
    dateRange: state.dateRange,
    objectPaths: state.objectPaths,
  });

  // Load the identity count when the drawer opens without loading table rows.
  useEffect(() => {
    if (!isOpen || !currentResource?.resource_arn) return;

    const request = {
      body: {
        // Reuse the filters shared with the activity graph CSV download.
        ...getActivityAccessFilters(
          state.dateRange,
          currentResource.resource_arn,
          state.objectPaths,
        ),
        limit: 1,
        offset: 0,
      },
    };
    const requestKey = JSON.stringify([
      activityGroupId,
      currentResource.resource_arn,
      request.body,
    ]);
    if (requestedActivityIdentityCountKey.current === requestKey) return;

    requestedActivityIdentityCountKey.current = requestKey;
    dispatch(actions.getActivityIdentityCount({ q: request }));
  }, [
    activityGroupId,
    currentResource?.resource_arn,
    dispatch,
    isOpen,
    state.dateRange,
    state.objectPaths,
  ]);

  useEffect(() => {
    if (!isOpen) {
      requestedActivityIdentityCountKey.current = undefined;
    }
  }, [isOpen]);

  const openPrincipalsMetadata = useCallback(() => {
    if (!currentResource) return;

    const currentResourceType = currentResource.resource_type ?? resourceType;
    updateActiveResource({
      resourceId: String(
        resourceInsight?.resource_id ?? currentResource.resource_name ?? '',
      ),
      resourceType: currentResourceType as NativeResources,
      uuid: resourceInsight?.uuid,
      accountId: String(
        resourceInsight?.account ?? currentResource.account_id ?? '',
      ),
      cloudProvider: CloudProvider.Aws,
    });
    onOpenMetadata(
      getTabIndex(
        currentResourceType as NativeResources,
        MetadataTabs.Principal,
      ),
    );
  }, [
    currentResource,
    getTabIndex,
    onOpenMetadata,
    resourceInsight,
    resourceType,
    updateActiveResource,
  ]);

  const openAccessGraph = useCallback(() => {
    if (!currentResource) return;

    const currentResourceType = currentResource.resource_type ?? resourceType;
    const resourceUuid = resourceInsight?.uuid;
    if (!resourceUuid) return;

    redirectToSecurityGraph({
      mapType: 'access',
      resourceType: String(currentResourceType),
      resourceUuid: String(resourceUuid),
      resourceId: String(
        resourceInsight?.resource_id ?? currentResource.resource_name ?? '',
      ),
      account: String(
        resourceInsight?.account ?? currentResource.account_id ?? '',
      ),
      sourceTab: MetadataTabs.Activity,
    });
  }, [currentResource, redirectToSecurityGraph, resourceInsight, resourceType]);

  const selectedResourcesRef = useRef(state.resource);
  selectedResourcesRef.current = state.resource;
  const bucketResourceId = resourceInsight?.uuid;
  const bucketAccountId = resourceInsight?.account;
  const bucketAccountIdentifier =
    resourceInsight?.account ?? currentResource?.account_id;
  const bucketCloudAccountId =
    accountMapping?.[bucketAccountIdentifier]?.cloud_account_id ??
    cloudAccountIDMapping?.[bucketAccountIdentifier]?.cloud_account_id;

  // Load contexts attached to the bucket shown in the activity graph.
  useEffect(() => {
    if (!isOpen || !bucketResourceId) {
      if (!isOpen) requestedActivityBucketContextsKey.current = undefined;
      return;
    }

    const requestKey = JSON.stringify([bucketResourceId, bucketAccountId]);
    if (requestedActivityBucketContextsKey.current === requestKey) return;
    requestedActivityBucketContextsKey.current = requestKey;

    dispatch(
      actions.getActivityBucketContexts({
        q: {
          resourceId: String(bucketResourceId),
          accountId: bucketAccountId ? [String(bucketAccountId)] : undefined,
        },
        onSuccess: r => {
          updateState({
            contexts: map(r, o => ({
              label: o.label_name,
              value: o.value ?? o.label_name,
              data: o,
            })),
          });
        },
      }),
    );
  }, [bucketAccountId, bucketResourceId, dispatch, isOpen]);

  const getActivityGraph = useCallback(
    (objectPaths, dateRange?: any) => {
      const range = dateRange ?? state?.dateRange;
      dispatch(
        actions.getActivityGraph({
          q: {
            body: {
              start_date: range?.from
                ? dayjs(range.from).format('YYYY-MM-DD')
                : undefined,
              end_date: range?.to
                ? dayjs(range.to).format('YYYY-MM-DD')
                : undefined,
              resource_ids: [currentResource?.resource_name!],
              contexts: state.contexts?.map(option => option.value),
              object_paths: objectPaths?.map(o => o?.value),
              timezone: dayjs.tz.guess(),
            },
          },
        }),
      );
    },
    [currentResource, state.contexts, dispatch, state?.dateRange],
  );

  // Use the bucket map populated by the activity-group request to restore the
  // selected paths and logging state, then request the graph once per drawer
  // open/resource combination.
  useEffect(() => {
    if (!isOpen || !activityGroupId || !currentResource?.resource_arn) {
      if (!isOpen) requestedActivityGraphKey.current = undefined;
      return;
    }

    const resourceArn = String(currentResource.resource_arn);
    const requestKey = `${activityGroupId}:${resourceArn}`;
    const bucket = activityGroupBucketsByArn[resourceArn];
    if (!bucket) return;

    const paths = flattenSelectedPaths(bucket.paths);
    updateState({
      objectPaths: paths,
      loggingStatusMessage: (bucket as typeof bucket & {
        logging_status_message?: string;
      }).logging_status_message,
    });

    if (requestedActivityGraphKey.current === requestKey) return;
    requestedActivityGraphKey.current = requestKey;
    getActivityGraph(paths);
  }, [
    activityGroupBucketsByArn,
    activityGroupId,
    currentResource?.resource_arn,
    getActivityGraph,
    isOpen,
  ]);

  const selectedObjectPaths = useMemo(() => {
    const bucket = activityObjectPaths.data?.results?.find(
      item =>
        String(item.bucket_name ?? '') ===
        String(currentResource?.resource_name ?? ''),
    );
    if (!bucket) return [];

    const options = flattenPaths(bucket?.paths ?? []);
    const normalizedSelectedPaths = normalizeSelectedFolders(
      state.objectPaths,
      options.length ? options : [ROOT_FOLDER_OPTION],
    );

    return normalizedSelectedPaths.length
      ? normalizedSelectedPaths
      : [ROOT_FOLDER_OPTION];
  }, [
    activityObjectPaths.data?.results,
    currentResource?.resource_name,
    state.objectPaths,
  ]);

  const objectPathOptions = useMemo(
    () =>
      map(activityObjectPaths.data?.results, bucket => {
        const paths = flattenPaths(bucket.paths ?? []);
        const selectedValues = new Set(
          selectedObjectPaths.map(path => path.value),
        );
        const hasSelectedRoot = selectedValues.has(ROOT_FOLDER_OPTION.value);
        const hasRootOption = paths.some(
          path => path.value === ROOT_FOLDER_OPTION.value,
        );
        const folderOptions = paths.length
          ? hasSelectedRoot && !hasRootOption
            ? [...paths, ROOT_FOLDER_OPTION]
            : paths
          : [ROOT_FOLDER_OPTION];
        const totalPathCount = folderOptions.length;
        const selectedPathCount = folderOptions.filter(path =>
          selectedValues.has(path.value),
        ).length;

        return {
          label: bucket.bucket_name ?? '-',
          showCheckbox: true,
          showOptionCount: false,
          isChecked: !!totalPathCount && selectedPathCount === totalPathCount,
          isIndeterminate:
            selectedPathCount > 0 && selectedPathCount < totalPathCount,
          options: getFolderOptionsWithSelection(
            folderOptions,
            selectedObjectPaths,
          ),
        };
      }),
    [activityObjectPaths.data?.results, selectedObjectPaths],
  );

  // Load object path options for the selected bucket.
  useEffect(() => {
    if (!isOpen || !currentResource?.resource_name || !bucketCloudAccountId) {
      if (!isOpen) requestedActivityObjectPathsKey.current = undefined;
      return;
    }

    const requestKey = JSON.stringify([
      currentResource.resource_arn,
      currentResource.resource_name,
      bucketCloudAccountId,
    ]);
    if (requestedActivityObjectPathsKey.current === requestKey) return;
    requestedActivityObjectPathsKey.current = requestKey;

    dispatch(
      actions.getActivityObjectPaths({
        q: {
          body: {
            buckets: [
              {
                bucket_name: currentResource?.resource_name,
                account_id: bucketCloudAccountId,
              },
            ],
          },
        },
      }),
    );
  }, [
    currentResource?.account_id,
    currentResource?.resource_arn,
    currentResource?.resource_name,
    bucketCloudAccountId,
    dispatch,
    isOpen,
  ]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      header={
        isFullWindow ? (
          <HStack w="full" justify="space-between" pr={12}>
            <StackedHeader
              upper="Activity Graph"
              lower={formatCloudEventsSummaryDateRange(state.dateRange) ?? '-'}
              type={IconTypes.Dependencies}
              border
            />
            <ActivityGraphControls
              isTableView={isTableView}
              isFullWindow={isFullWindow}
              onToggleFullWindow={() => setIsFullWindow(!isFullWindow)}
              onToggleTable={() => setIsTableView(!isTableView)}
              onShowGraph={() => setIsTableView(false)}
              onShowPrincipals={openPrincipalsMetadata}
              onShowAccessGraph={openAccessGraph}
              onDownload={downloadActivityGraph}
            />
          </HStack>
        ) : (
          <PageHeaderWithIcon
            label="Show Activity"
            iconType={resourceType}
            iconColor={customTheme.colors.orange['400']}
            useCustomColor
            border
            iconBgColor={colorOpacity(customTheme.colors.orange['400'], 0.1)}
            borderColor={customTheme.colors.orange['400']}
          />
        )
      }
      body={
        <Stack
          h="full"
          minH={0}
          w="full"
          spacing={5}
          py={3}
          overflowY="auto"
          overflowX="hidden"
        >
          {!isFullWindow && (
            <Box flexShrink={0}>
              <OutlinedForm
                fields={[
                  {
                    type: 'object',
                    name: 'resourceFilters',
                    label: undefined,
                    styles: {
                      field: { border: 'none', p: 0 },
                      skeleton: { gridColumn: '1 / -1' },
                      objectContainer: {
                        display: 'grid',
                        gridTemplateColumns: {
                          base: 'minmax(0, 1fr)',
                          md: 'repeat(5, minmax(0, 1fr))',
                        },
                        gap: 3,
                        spacing: 0,
                      },
                      propertyContainer: [
                        { gridColumn: { base: 'span 1', md: 'span 1' } },
                        { gridColumn: { base: 'span 1', md: 'span 2' } },
                        {
                          gridColumn: { base: 'span 1', md: 'span 1' },
                          w: 'full',
                        },
                      ],
                    },
                    properties: [
                      {
                        type: 'display',
                        name: 'resourceType',
                        label: 'Resource Type',
                        value: renderIconWithLabel(
                          resourceType,
                          getCloudNativeName(resourceType),
                          getIconColor(resourceType),
                        ),
                      },
                      {
                        type: 'display',
                        name: 'resource',
                        label: 'Resource',
                        value: renderIconWithLabel(
                          resourceType,
                          currentResource?.resource_name,
                          getIconColor(resourceType),
                        ),
                      },
                      {
                        type: 'select',
                        name: 'contexts',
                        label: 'Ariksa Context',
                        options: state.contexts,
                        value: state.contexts,
                        isLoading: activityBucketContexts.isLoading,
                        placeholder: 'No contexts',
                        selectProps: {
                          isMulti: true,
                          selectedMessage: 'labels',
                          showTotalSelected: true,
                          showClearIndicator: false,
                          showIconInValueContainer: false,
                        },
                      },
                    ],
                  },
                  {
                    type: 'object',
                    name: 'activityFilters',
                    label: undefined,
                    styles: {
                      field: { border: 'none', p: 0, h: 'fit-content' },
                      skeleton: { gridColumn: '1 / -1' },
                      objectContainer: {
                        display: 'grid',
                        gridTemplateColumns: {
                          base: 'minmax(0, 1fr)',
                          md: 'repeat(5, minmax(0, 1fr))',
                        },
                        gap: 3,
                        spacing: 0,
                      },
                      propertyContainer: [
                        {
                          gridColumn: { base: 'span 1', md: 'span 2' },
                          w: 'full',
                        },
                        { gridColumn: { base: 'span 1', md: 'span 1' } },
                      ],
                    },
                    properties: [
                      {
                        type: 'select',
                        name: 'objectPaths',
                        label: 'Configured Folders That Were Accessed',
                        options: objectPathOptions,
                        value: selectedObjectPaths,

                        placeholder: 'No folders selected',
                        selectProps: {
                          isMulti: true,
                          showGroupCollapse: true,
                          showTotalSelected: true,
                          showClearIndicator: false,
                          showSelectedAtBottomOutside: true,
                          showMoreSelectedOptions: true,
                          selectedOptionsLimit: 3,
                          selectedOptionsStyles: {
                            maxH: '210px',
                            overflowY: 'auto',
                            pr: 1,
                          },
                          selectedOptionTagStyles: {
                            tag: {
                              bg: colorOpacity(
                                customTheme.colors.blue['300'],
                                0.1,
                              ),
                              border: '1px solid',
                              borderColor: customTheme.colors.blue['300'],
                            },
                            label: {
                              color: customTheme.colors.blue['300'],
                            },
                            closeButton: {
                              color: customTheme.colors.blue['300'],
                            },
                          },
                          isLoading: activityObjectPaths.isLoading,
                          selectedCount: getSelectedFolderCount(
                            selectedObjectPaths,
                          ),
                          selectedOptionLabel: option => option.value,
                          selectedMessage:
                            'folder' +
                            (getSelectedFolderCount(selectedObjectPaths) === 1
                              ? ''
                              : 's'),
                          components: {
                            MenuList: ObjectPathMenuList,
                          },
                          styles: {
                            option: (
                              provided,
                              { data, isFocused, isSelected },
                            ) => ({
                              ...provided,
                              color: isSelected
                                ? customTheme.colors.blue['300']
                                : '#000',
                              backgroundColor:
                                isSelected || !isFocused
                                  ? '#fff'
                                  : colorMap.primary(50),
                              paddingLeft: data?.depth
                                ? `${data.depth * 24}px`
                                : provided.paddingLeft,
                              '&:active': {
                                backgroundColor: isSelected
                                  ? '#fff'
                                  : colorMap.primary(100),
                              },
                              '&:hover': {
                                backgroundColor: isSelected
                                  ? '#fff'
                                  : colorMap.primary(100),
                              },
                            }),
                          },
                        },
                      },
                      {
                        type: 'dateRange',
                        name: 'dateRange',
                        label: 'Date Range',
                        dateProps: {
                          disabled: [
                            {
                              before: dayjs()
                                .subtract(90, 'day')
                                .startOf('day')
                                .toDate(),
                            },
                            { after: new Date() },
                          ],
                          mode: 'range',
                          onSelect: dateRange => {
                            updateState({ dateRange });
                            getActivityGraph(state.objectPaths, dateRange);
                          },
                          selected: state.dateRange,
                        },
                      },
                    ],
                  },
                ]}
                styles={{
                  fields: {
                    display: 'grid',
                    gridTemplateColumns: {
                      base: 'minmax(0, 1fr)',
                      md: 'repeat(5, minmax(0, 1fr))',
                    },
                    gap: 3,
                    spacing: 0,
                  },
                }}
              />
            </Box>
          )}

          {!isFullWindow && (
            <Box flexShrink={0}>
              <HStack w="full" justify="space-between">
                <StackedHeader
                  upper="Activity Graph"
                  lower={
                    formatCloudEventsSummaryDateRange(state.dateRange) ?? '-'
                  }
                />

                <ActivityGraphControls
                  isTableView={isTableView}
                  isFullWindow={isFullWindow}
                  onToggleFullWindow={() => setIsFullWindow(!isFullWindow)}
                  onToggleTable={() => setIsTableView(!isTableView)}
                  onShowGraph={() => setIsTableView(false)}
                  onShowPrincipals={openPrincipalsMetadata}
                  onShowAccessGraph={openAccessGraph}
                  onDownload={downloadActivityGraph}
                />
              </HStack>
            </Box>
          )}
          {state.loggingStatusMessage && (
            <InformationAlert
              color="critical"
              bg={colorOpacity(customTheme.colors.critical, 0.1)}
              py={1.5}
              iconSize={7}
              spacing={2}
              icon={<ExclamationCircleIcon />}
              description={state.loggingStatusMessage}
            />
          )}
          <HStack flex={1} flexShrink={0} h="full" minH="500px">
            <BorderedBox
              flex={1}
              h="full"
              minH={0}
              w="full"
              pos="relative"
              overflow="hidden"
            >
              <Stack h="full" w="full" pos="relative" spacing={4}>
                {isTableView ? (
                  <ActivityGraphTable
                    currentResource={currentResource}
                    dateRange={state.dateRange}
                    objectPaths={state.objectPaths}
                  />
                ) : (
                  <ActivityGraph
                    activityGraph={activityGraph}
                    hasSelectedResource={!isEmpty(currentResource)}
                  />
                )}
              </Stack>
            </BorderedBox>
            {!isFullWindow && (
              <AccessSummary
                currentResource={currentResource}
                insight={resourceInsight}
                resourceType={resourceType}
                isOpen={isOpen}
              />
            )}
          </HStack>
        </Stack>
      }
      closeButton
      styles={{
        content: {
          maxW: isFullWindow ? 'full' : '1550px',
          w: isFullWindow ? 'full' : undefined,
        },
        body: { overflow: 'hidden' },
        drawer: {
          blockScrollOnMount: false,
          trapFocus: false,
        },
      }}
    />
  );
};
